'use client';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import config from './config';
import { auth } from '../fire-base/FirebaseAuth';
import { toaster } from './toast';

// One-shot per recovery window so concurrent 401s collapse into a single
// toast + redirect. Reset by the success interceptor below (any 2xx
// proves a usable session is back) and by a real navigation to
// /auth/login that drops the module.
let sessionExpiredHandled = false;

const handleSessionExpired = () => {
  if (typeof window === 'undefined') return;
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  try {
    toaster('error', 'Session expired, please login !');
  } catch {
    // toast may not be initialized
  }
  if (!window.location.pathname.startsWith('/auth/login')) {
    window.location.href = '/auth/login';
  }
};

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retried?: boolean };

// Two-arg shape preserved: callers in *Model files pass header overrides
// (notably `Content-Type: multipart/form-data` for file uploads) as the
// first arg, and the second selects anonymous mode (no Firebase token
// injection). Collapsing to a single boolean broke uploads because the
// header object became truthy and short-circuited the auth interceptors.
const http = (headers?: any, anonymous?: boolean): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.BASE_API_URL,
    timeout: config.TIMEOUT,
    headers: { ...(headers || {}) },
  });

  if (anonymous) return instance;

  instance.interceptors.request.use(async (req: InternalAxiosRequestConfig) => {
    // Wait for Firebase to restore its persisted session before reading
    // currentUser. Without this, requests fired during the first tick after
    // page load go out unauthenticated and trip a spurious 401.
    await auth.authStateReady();

    const user = auth.currentUser;
    if (!user) {
      // No signed-in user. Cancel locally rather than firing without auth —
      // a 401 here would force-redirect a user who is already on a public page.
      const controller = new AbortController();
      controller.abort();
      req.signal = controller.signal;
      return req;
    }

    try {
      const token = await user.getIdToken();
      req.headers.set('Authorization', `Bearer ${token}`);
    } catch {
      // Transient refresh failure. Send without the header; the response
      // interceptor will try a forced refresh + retry before giving up.
    }
    return req;
  });

  instance.interceptors.response.use(
    (res) => {
      // A successful response means the session is healthy again. Clear the
      // one-shot flag so a later 401 in the same tab (e.g. after the token
      // is revoked server-side) re-triggers the toast + redirect.
      if (sessionExpiredHandled) sessionExpiredHandled = false;
      return res;
    },
    async (err: AxiosError) => {
      if (err?.response?.status !== 401) {
        return Promise.reject(err);
      }

      const original = err.config as RetriableRequestConfig | undefined;
      const user = auth.currentUser;

      // One retry with a forced token refresh covers stale-cached tokens,
      // near-expiry tokens, and transient refresh failures.
      if (original && !original._retried && user) {
        original._retried = true;
        try {
          await user.getIdToken(true);
          return instance.request(original);
        } catch {
          // Force-refresh genuinely failed — fall through.
        }
      }

      handleSessionExpired();
      return Promise.reject(err);
    }
  );

  return instance;
};

export default http;
